/**
 * InviteAccept — página pública para aceptar invitaciones a una organización.
 *
 * Flujo:
 *   1. Lee ?token=<token> de la URL.
 *   2. Llama a GET /api/v1/orgs/invitations/preview?token=<token> (sin auth).
 *   3. Si ya está autenticado → muestra detalles + botón "Aceptar".
 *   4. Si NO está autenticado → muestra detalles + botones "Crear cuenta" / "Iniciar sesión".
 *      Guarda el token en sessionStorage para recuperarlo tras el login/registro.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Button, Center, VStack, Heading, Text, Badge,
  Spinner, Alert, AlertIcon, HStack, Divider,
} from '@chakra-ui/react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { previewInvitation, acceptInvitation } from '../services/apiClient';

const ROLE_LABELS = {
  admin: 'Admin',
  analyst: 'Analyst',
  reader: 'Reader',
};

const ROLE_COLORS = {
  admin: 'red',
  analyst: 'blue',
  reader: 'gray',
};

/** Comprueba si hay un JWT válido y no expirado en localStorage. */
function isLoggedIn() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

const PENDING_INVITE_KEY = 'pendingInviteToken';

const InviteAccept = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const loggedIn = isLoggedIn();

  useEffect(() => {
    if (!token) {
      setError('Token de invitación no encontrado en la URL.');
      setLoading(false);
      return;
    }
    previewInvitation(token)
      .then(setPreview)
      .catch(() => setError('Esta invitación no existe, ya fue usada o ha expirado.'))
      .finally(() => setLoading(false));
  }, [token]);

  // Auto-accept si venía de login/registro con token pendiente
  useEffect(() => {
    const pending = sessionStorage.getItem(PENDING_INVITE_KEY);
    if (pending && loggedIn && preview && !accepted) {
      sessionStorage.removeItem(PENDING_INVITE_KEY);
      handleAccept();
    }
  }, [loggedIn, preview]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await acceptInvitation(preview.org_id, token);
      setAccepted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al aceptar la invitación.');
    } finally {
      setAccepting(false);
    }
  };

  const handleGoLogin = () => {
    sessionStorage.setItem(PENDING_INVITE_KEY, token);
    navigate('/login');
  };

  if (loading) {
    return (
      <Center minH="60vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (accepted) {
    return (
      <Center minH="60vh">
        <VStack spacing={4} maxW="400px" textAlign="center">
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            ¡Ya eres miembro de <strong>&nbsp;{preview?.org_name}&nbsp;</strong>!
          </Alert>
          <Button colorScheme="blue" onClick={() => navigate('/')}>
            Ir al panel
          </Button>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Center minH="60vh">
        <VStack spacing={4} maxW="400px" textAlign="center">
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
          <Button variant="link" onClick={() => navigate('/login')}>
            Iniciar sesión
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Center minH="60vh" px={4}>
      <Box
        maxW="420px"
        w="100%"
        borderWidth="1px"
        borderRadius="xl"
        p={8}
        boxShadow="lg"
      >
        <VStack spacing={5} align="stretch">
          <Heading size="md" textAlign="center">
            Invitación a organización
          </Heading>

          <Divider />

          <VStack spacing={2} align="start">
            <Text fontSize="sm" color="gray.500">Organización</Text>
            <Text fontWeight="bold" fontSize="lg">{preview.org_name}</Text>
          </VStack>

          <VStack spacing={2} align="start">
            <Text fontSize="sm" color="gray.500">Rol asignado</Text>
            <Badge colorScheme={ROLE_COLORS[preview.role] || 'gray'} fontSize="sm" px={2} py={1}>
              {ROLE_LABELS[preview.role] || preview.role}
            </Badge>
          </VStack>

          <VStack spacing={2} align="start">
            <Text fontSize="sm" color="gray.500">Email</Text>
            <Text>{preview.invitee_email}</Text>
          </VStack>

          <Divider />

          {loggedIn ? (
            <Button
              colorScheme="blue"
              size="lg"
              onClick={handleAccept}
              isLoading={accepting}
              loadingText="Aceptando..."
            >
              Aceptar invitación
            </Button>
          ) : (
            <VStack spacing={3}>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                Para aceptar esta invitación necesitas una cuenta.
              </Text>
              <HStack w="100%" spacing={3}>
                <Button flex={1} colorScheme="blue" onClick={handleGoLogin}>
                  Iniciar sesión
                </Button>
                <Button flex={1} variant="outline" onClick={handleGoLogin}>
                  Crear cuenta
                </Button>
              </HStack>
              <Text fontSize="xs" color="gray.400" textAlign="center">
                Tras iniciar sesión, la invitación se aceptará automáticamente.
              </Text>
            </VStack>
          )}
        </VStack>
      </Box>
    </Center>
  );
};

export default InviteAccept;
